import Fuse from 'fuse.js';
import { getAllPatients, setPtCount } from '../store/idbHandler';
import { checkIfValidPhoneNumber, dumpDynamicFormAutoSuggest } from './utils';
import { Patient, getPtFullName } from '../store/types/patient';
import { track } from '../tracking';

export const fuseConfig = {
    keys: ['_name', '_phone', '_uhid', '_healthIds'],
    threshold: 0.1,
    ignoreLocation: true,
    useExtendedSearch: true,
    shouldSort: false,
};

type FP = Patient & {
    _name: string;
    _phone?: string;
    _uhid?: string | number | undefined;
    _healthIds?: string[];
};

let patientsFuse: Fuse<FP> | undefined;
let allPtCount: number | undefined;
let onAppPtsCount: number | undefined;
let ptsWithAbha: number | undefined;
let abhaPtsWithNoLinkedRecords: number | undefined;

export const indexAllPatients = async (): Promise<{
    patientsFuse: Fuse<FP>;
    allPtCount: number;
    onAppPtsCount: number;
    ptsWithAbha: number;
    abhaPtsWithNoLinkedRecords: number;
}> => {
    if (
        patientsFuse &&
        allPtCount !== undefined &&
        onAppPtsCount !== undefined &&
        ptsWithAbha !== undefined &&
        abhaPtsWithNoLinkedRecords !== undefined
    ) {
        return { patientsFuse, allPtCount, onAppPtsCount, ptsWithAbha, abhaPtsWithNoLinkedRecords };
    }

    const value = (await getAllPatients()) || [];
    console.timeEnd('dbCallAllPt');
    track('dbCallAllPt', { count: value?.length.toString() });

    const allPt = value?.filter((t) => !t.archived) || [];

    setPtCount(allPt?.length);

    allPtCount = allPt.length;
    onAppPtsCount = allPt.filter((a) => a.personal.onApp).length;
    ptsWithAbha = allPt.filter((p) => p.abha?.id).length;
    abhaPtsWithNoLinkedRecords = allPt.filter((p) => !p.abha?.records).length;

    patientsFuse = new Fuse(
        allPt.map((t) => ({
            ...t,

            _name: getPtFullName(t),
            _phone: t.personal.phone?.n,
            _uhid: t.formData?.find((f) => f.key === 'uhid')?.value,
            _healthIds: t.abha?.id ? [t.abha?.id] : undefined,
        })),
        fuseConfig,
    );

    dumpDynamicFormAutoSuggest(value);

    return { patientsFuse, allPtCount, onAppPtsCount, ptsWithAbha, abhaPtsWithNoLinkedRecords };
};

export const addPatientToIndex = async (p: Patient[]): Promise<void> => {
    await indexAllPatients();

    p.forEach((pt) => {
        patientsFuse?.remove((d) => d.localId === pt.localId);

        if (!pt.archived) {
            patientsFuse?.add({
                ...pt,

                _name: getPtFullName(pt),
                _phone: pt.personal.phone?.n,
                _uhid: pt.formData?.find((f) => f.key === 'uhid')?.value,
            });
        }
    });
};

export function getSearchResults(
    searchInput: string,
    patientsfuse: Fuse<Patient>,
    limit?: number,
): { filteredPatients: Patient[]; inputType: 'name' | 'phoneNumber' | 'uhid' } {
    let filteredPatients: Patient[] = [];
    let inputType: 'name' | 'phoneNumber' | 'uhid' = 'name';
    const isPlusPrefixed = searchInput.startsWith('+');
    const cleanedInput = searchInput.replace(/^\+91/, '').trim();
    const isAllDigits = /^\d+$/.test(cleanedInput);
    if (
        !isNaN(parseInt(searchInput.replace('+91', ''))) &&
        searchInput.length > 5 &&
        cleanedInput.length <= 10 &&
        isAllDigits
    ) {
        inputType = 'phoneNumber';
        const isValidPhone = isPlusPrefixed
            ? (() => {
                  const match = searchInput.match(/^\+(\d{1,4})(\d{6,15})$/);
                  if (!match) return false;
                  return checkIfValidPhoneNumber(`+${match[1]}`, match[2]);
              })()
            : checkIfValidPhoneNumber('+91', cleanedInput, 'IN');

        // exact match

        if (
            searchInput.replace('+91', '').length === 10 &&
            /^\d+$/.test(searchInput) &&
            cleanedInput.length === 10 &&
            isValidPhone
        ) {
            filteredPatients = patientsfuse.search(`=${searchInput}`).map((option) => option.item);
        } else {
            filteredPatients = patientsfuse
                .search({
                    $and: [
                        { _phone: searchInput.replace('+91', '') }, // Fuzzy "old war"
                    ],
                })
                .map((option) => option.item);
        }
    } else if (!isNaN(parseInt(searchInput.replace('+91', ''))) && searchInput.length < 5) {
        inputType = 'uhid';
        filteredPatients = patientsfuse
            .search({
                $and: [
                    { _uhid: `=${searchInput.replace('+91', '')}` }, // Fuzzy "old war"
                ],
            })
            .map((option) => option.item);
    } else {
        filteredPatients = [];
    }

    const allPtIds = new Set(filteredPatients.map((t: any) => t.localId));

    patientsfuse
        .search(searchInput.replace('+91', ''), { limit: limit ?? 100 })
        .forEach((option: any) => {
            if (!allPtIds.has(option.item.localId)) {
                filteredPatients.push(option.item);
            }
        });

    filteredPatients = filteredPatients.filter((p) => !p.archived);

    return { filteredPatients, inputType };
}

export function getSearchType(
    searchInput: string,
    prefix?: string,
): 'name' | 'phoneNumber' | 'uhid' {
    const cleanedInput = searchInput.replace(/^\+91/, '').trim();
    const isAllDigits = /^\d+$/.test(cleanedInput);

    if (
        typeof prefix === 'string' &&
        prefix.length > 0 &&
        searchInput.startsWith(prefix) &&
        !(
            searchInput.replace(prefix, '').length > 0 &&
            isNaN(parseInt(searchInput.replace(prefix, '')))
        ) && // Check if rest of the characters are numeric
        searchInput.length < 12
    ) {
        return 'uhid';
    } else if (
        !isNaN(parseInt(searchInput.replace('+91', ''))) &&
        searchInput.length > 2 &&
        cleanedInput.length <= 10 &&
        isAllDigits
    ) {
        return 'phoneNumber';
    } else {
        return 'name';
    }
}

export const getAllPtCount = async (): Promise<number | undefined> => {
    return allPtCount;
};

export const getOnAppPtCount = async (): Promise<number | undefined> => {
    return onAppPtsCount;
};

export const getPtsWithAbha = (): number | undefined => {
    return ptsWithAbha;
};

export const getPtsWithNoLinkedRecords = async (): Promise<number | undefined> => {
    return abhaPtsWithNoLinkedRecords;
};

export const flushPatientFuseInstance = (): void => {
    patientsFuse = undefined;
    allPtCount = undefined;
    onAppPtsCount = undefined;
    ptsWithAbha = undefined;
    abhaPtsWithNoLinkedRecords = undefined;
};
